'use client';

import {
  type RefObject,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';

import {
  type PostStats,
  applyFetchedCounts,
  applyFetchedLiked,
  ensurePostStats,
  getPostStats,
  patchLocalStats,
  postStatsKey,
  subscribePostStats,
} from '../../functions/postStatsStore';
import { getCachedLiked, setCachedLiked } from '../functions/likedPostsCache';
import { fetchPostLiked, fetchPostStats } from '../functions/postStatsApi';
import { likePost } from '../mutations/likePost';
import { unlikePost } from '../mutations/unlikePost';

interface UsePostStats extends PostStats {
  /** Attach to the footer — stats load just before it scrolls into view. */
  footerRef: RefObject<HTMLDivElement | null>;
  /** Optimistic like toggle (bounces anonymous viewers to login). */
  toggleLike: () => void;
}

/** Public counts fetched at most ONCE per post across all mounted surfaces. */
const statsLoaded = new Set<string>();

async function loadPublicCounts(
  key: string,
  storeId: string,
  postId: string,
): Promise<void> {
  if (statsLoaded.has(key)) return;
  statsLoaded.add(key);
  try {
    const stats = await fetchPostStats(storeId, postId);
    applyFetchedCounts(key, stats);
  } catch (error) {
    statsLoaded.delete(key);
    console.error('[usePostStats] public counts fetch failed', error);
  }
}

/** Viewer `liked` fetched at most ONCE per (viewer, post) — IndexedDB, then API on a miss. */
const likedLoaded = new Set<string>();

async function loadViewerLiked(
  uid: string,
  key: string,
  storeId: string,
  postId: string,
): Promise<void> {
  const guard = `${uid}:${key}`;
  if (likedLoaded.has(guard)) return;
  likedLoaded.add(guard);
  try {
    let liked = await getCachedLiked(uid, postId);
    if (liked == null) {
      liked = await fetchPostLiked(storeId, postId).then(
        result => result.liked,
      );
      if (liked != null) void setCachedLiked(uid, postId, liked);
    }
    if (liked != null) applyFetchedLiked(key, liked);
  } catch (error) {
    likedLoaded.delete(guard);
    console.error('[usePostStats] viewer liked fetch failed', error);
  }
}

/**
 * Post engagement stats (like + comment counts, viewer `liked`) backed by the
 * shared reactive {@link postStatsStore}, so every surface for a post stays in
 * lockstep. Snapshot-first, fetch-on-viewport:
 *   - Seeds the shared store from the post SNAPSHOT (the cached feed value) and
 *     renders it immediately.
 *   - Just before the footer enters the viewport (IntersectionObserver), pulls
 *     fresh PUBLIC counts from `/api/bio/posts/stats` (server-cached for days,
 *     tag-busted on mutation) and the viewer's `liked` from IndexedDB — falling
 *     back to `/api/bio/posts/liked` only on a cache miss. Both are deduped
 *     per-post across mounts.
 *   - A like toggle writes the shared store optimistically (so the feed card AND
 *     the detail modal update together) and locks out the lagging server counts;
 *     the like route busts the stats cache for the next fresh load.
 */
export function usePostStats({
  storeId,
  postId,
  snapshot,
}: {
  storeId: string;
  postId: string;
  snapshot: PostStats;
}): UsePostStats {
  const login = useLogin();
  const currentUserId = useCurrentUserId();
  const key = postStatsKey(storeId, postId);
  ensurePostStats(key, snapshot);
  const stats = useSyncExternalStore(
    listener => subscribePostStats(key, listener),
    () => getPostStats(key) ?? snapshot,
    () => snapshot,
  );

  const footerRef = useRef<HTMLDivElement | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = footerRef.current;
    if (!el || seen) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [seen]);

  useEffect(() => {
    if (!seen) return;
    void loadPublicCounts(key, storeId, postId);
  }, [seen, key, storeId, postId]);

  useEffect(() => {
    if (!seen || !currentUserId) return;
    void loadViewerLiked(currentUserId, key, storeId, postId);
  }, [seen, currentUserId, key, storeId, postId]);

  const toggleLike = () => {
    // Auth loads lazily — `currentUserId` is `undefined` until it resolves.
    // Ignore a tap made before then, else a signed-in viewer would be
    // hard-redirected to /login. Only a resolved `null` (anonymous) logs in.
    if (currentUserId === undefined) return;
    if (currentUserId === null) {
      login();
      return;
    }
    const uid = currentUserId;
    const current = getPostStats(key) ?? snapshot;
    const nextLiked = !current.liked;
    const nextCount = Math.max(0, current.likeCount + (nextLiked ? 1 : -1));
    patchLocalStats(key, { liked: nextLiked, likeCount: nextCount });
    void setCachedLiked(uid, postId, nextLiked);
    const mutate = nextLiked ? likePost : unlikePost;
    // Optimistic: a plain async op, NOT a transition — a transition would batch
    // away the immediate paint (see CLAUDE.md optimistic-update exception).
    mutate(storeId, postId).catch((error: unknown) => {
      console.error('[usePostStats] like toggle failed', error);
      patchLocalStats(key, {
        liked: current.liked,
        likeCount: current.likeCount,
      });
      void setCachedLiked(uid, postId, current.liked);
    });
  };

  return {
    liked: stats.liked,
    likeCount: stats.likeCount,
    commentCount: stats.commentCount,
    footerRef,
    toggleLike,
  };
}
